import React, { useState, useEffect } from "react";
import { ArrowLeft, Share2, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import PublicLayout from "./PublicLayout";
import { fetchSampleManuscript, fetchSampleTopSimilarPapers, downloadSampleExportCall } from "../service/SampleService";
import { calculateOverallScore, sectionColor } from "../service/Operations";
import NetworkCanvas from "./NetworkCanvas";

const networkNodes = [
    { id: "your", label: "Your Paper", x: 46, y: 48, size: 14, highlight: true },
    { id: "a", x: 22, y: 28, size: 8 },
    { id: "b", x: 68, y: 22, size: 9 },
    { id: "c", x: 75, y: 58, size: 7 },
    { id: "d", x: 30, y: 68, size: 8 },
    { id: "e", x: 55, y: 78, size: 6 },
    { id: "f", x: 14, y: 52, size: 6 },
    { id: "g", x: 60, y: 38, size: 7 },
    { id: "h", x: 38, y: 18, size: 6 },
];

const networkEdges = [
    ["your", "a"], ["your", "b"], ["your", "c"], ["your", "d"],
    ["your", "g"], ["a", "f"], ["a", "d"], ["b", "g"], ["b", "h"],
    ["c", "e"], ["d", "e"],
];

function NetworkGraph() {
    const nodeMap = Object.fromEntries(networkNodes.map(n => [n.id, n]));
    return (
        <div className="network-graph-wrap">
            <svg className="network-svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
                {networkEdges.map(([a, b], i) => (
                    <line key={i} x1={nodeMap[a].x} y1={nodeMap[a].y} x2={nodeMap[b].x} y2={nodeMap[b].y} stroke="rgba(160,220,180,0.25)" strokeWidth="0.5" />
                ))}
                {networkNodes.map(n => (
                    <circle key={n.id} cx={n.x} cy={n.y} r={n.size / 2} fill={n.highlight ? "#5ecfa0" : "rgba(160,220,180,0.55)"} />
                ))}
                <text x={46} y={43} textAnchor="middle" fontSize="3.5" fill="white" fontWeight="bold">Your Paper</text>
            </svg>
        </div>
    );
}

export default function SampleSubmission() {
    const navigate = useNavigate();
    const [paper, setPaper] = useState(null);
    const [similarPapers, setSimilarPapers] = useState([]);
    const [hasGraph, setHasGraph] = useState(false);
    const [loading, setLoading] = useState(true);
    const [similarLoading, setSimilarLoading] = useState(true);
    const [shareModal, setShareModal] = useState(false);
    const [shareEmail, setShareEmail] = useState("");
    const [exporting, setExporting] = useState(false);
    const [shareStatus, setShareStatus] = useState(null);

    const [smallScreen, setSmallScreen] = useState(window.innerWidth < 991);

    useEffect(() => {
        const onResize = () => {
            const small = window.innerWidth < 991;
            setSmallScreen(small);
            if (small) setLoading(false);
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const handleExportDownload = async () => {
        setExporting(true);
        try {
            const { blob, disposition } = await downloadSampleExportCall();
            const filename = disposition?.split("filename=")[1]?.replace(/"/g, "").trim() || "sample-report.pdf";
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = filename; a.click();
            URL.revokeObjectURL(url);
        } catch {
        } finally {
            setExporting(false);
        }
    };

    useEffect(() => {
        fetchSampleManuscript()
            .then(res => {
                if (res.manuscript) setPaper(res.manuscript);
                setHasGraph(!!res.manuscript?.graphKey?.trim())
                if (res.hasSimilarPapers) {
                    fetchSampleTopSimilarPapers()
                        .then(r => setSimilarPapers(r.similarPapers ?? []))
                        .catch(() => {})
                        .finally(() => setSimilarLoading(false));
                } else {
                    setSimilarLoading(false);
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const scoreCards = paper ? [
        { label: "Novelty", value: paper.noveltyScore },
        { label: "Methodology", value: paper.methodScore },
        { label: "Impact", value: paper.impactScore },
        { label: "Overall", value: calculateOverallScore(paper) },
    ] : [];

    return (
        <PublicLayout activePage="Upload Paper">
                        <div className="upload-main">
                            <button className="chpw-back" onClick={() => navigate("/upload")}>
                                <ArrowLeft size={14} />
                                Back to Upload
                            </button>

                            <div className="upload-page-title-row">
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    {loading ? (
                                        <div className="skeleton" style={{ height: 22, width: "60%", marginBottom: 8 }} />
                                    ) : (
                                        <>
                                            <div className="sample-badge">Sample Report</div>
                                            <h1 className="upload-page-heading">{paper?.title || paper?.name}</h1>
                                            {paper?.authors && <p className="upload-page-meta">{paper.authors}</p>}
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Score cards */}
                            <div className="submission-scores">
                                {loading ? (
                                    Array.from({ length: 4 }).map((_, i) => (
                                        <div key={i} className="submission-score-card">
                                            <div className="skeleton" style={{ height: 11, width: "55%", margin: "0 auto 10px" }} />
                                            <div className="skeleton" style={{ height: 32, width: "42%", margin: "0 auto 8px" }} />
                                        </div>
                                    ))
                                ) : scoreCards.map((s) => (
                                    <div key={s.label} className="submission-score-card">
                                        <p className="submission-score-label">{s.label}</p>
                                        <p className="submission-score-value">{s.value}</p>
                                    </div>
                                ))}
                            </div>

                            {/* Action buttons */}
                            {!loading && (
                                <div className="sub-actions-row">
                                    <button className="sub-action-btn sub-action-outline" onClick={handleExportDownload} disabled={exporting}>
                                        <Share2 size={13} />
                                        {exporting ? "Downloading..." : "Export Report"}
                                    </button>
                                    <button className="sub-action-btn sub-action-outline" onClick={() => { setShareModal(true); setShareStatus(null); setShareEmail(""); }}>
                                        <Send size={13} />
                                        Share Report
                                    </button>
                                </div>
                            )}

                            {/* Research Network */}
                            {!loading && <div className="submissions-card">
                                {hasGraph && !smallScreen &&
                                    <>
                                        <p className="similar-heading" style={{ padding: "16px 18px 0" }}>Research Network</p>
                                        <div
                                            className="submission-network-graph-full submission-network-clickable"
                                            onClick={() => navigate("/sample/network")}
                                            title="View full network"
                                        >
                                            <div style={{ borderRadius: "12px", height: "25vh", position: "relative", background: "#0f1a14", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                <NetworkCanvas />
                                                <div className="submission-network-view-hint">View Full Network →</div>
                                            </div>
                                        </div>
                                    </>
                                }
                                <div className="submission-similar-list">
                                    <p className="similar-heading" style={{ marginBottom: 10 }}>Top Similar Papers</p>
                                    {similarLoading ? (
                                        Array.from({ length: 3 }).map((_, i) => (
                                            <div key={i} className="submission-similar-item">
                                                <div className="submission-similar-info">
                                                    <div className="skeleton" style={{ height: 12, width: "75%", marginBottom: 7 }} />
                                                    <div className="skeleton" style={{ height: 10, width: "50%" }} />
                                                </div>
                                                <div className="submission-similar-actions">
                                                    <div className="skeleton" style={{ height: 22, width: 32, borderRadius: 8 }} />
                                                </div>
                                            </div>
                                        ))
                                    ) : similarPapers.length > 0 ? similarPapers.map((p) => (
                                        <div key={p.id} className="submission-similar-item" style={{ cursor: "pointer" }} onClick={() => navigate("/sample/similar-works")}>
                                            <div className="submission-similar-info">
                                                <p className="submission-similar-title">{p.articleTitle}</p>
                                                {p.section && (
                                                    <div style={{ marginTop: 4 }}>
                                                        <span className="similar-works-section-badge" style={sectionColor(p.section)}>{p.section}</span>
                                                    </div>
                                                )}
                                                <p className="submission-similar-authors" style={{ marginTop: 4 }}>{p.authors}</p>
                                            </div>
                                            <div className="submission-similar-actions">
                                                <span className="submission-similar-score">{Math.round(p.similarity * 100)}%</span>
                                            </div>
                                        </div>
                                    )) : (
                                        <p className="settings-empty">No similar papers found.</p>
                                    )}
                                </div>
                            </div>}
                        </div>

            {/* Share modal */}
            {shareModal && (
                <div className="share-modal-overlay" onClick={() => setShareModal(false)}>
                    <div className="share-modal-tile" onClick={e => e.stopPropagation()}>
                        <div className="share-modal-header">
                            <p className="share-modal-title">Share Report</p>
                            <button className="share-modal-close" onClick={() => setShareModal(false)}>×</button>
                        </div>
                        <div className="share-modal-body">
                            {shareStatus && (
                                <p className={shareStatus.type === "success" ? "chpw-success" : "chpw-error"} style={{ marginBottom: 12 }}>
                                    {shareStatus.msg}
                                </p>
                            )}
                            <form onSubmit={(e) => { e.preventDefault(); setShareStatus({ type: "success", msg: `Report sent to ${shareEmail}.` }); setShareEmail(""); }}>
                                <input
                                    type="email"
                                    className="share-email-input"
                                    placeholder="recipient@email.com"
                                    required
                                    value={shareEmail}
                                    onChange={e => setShareEmail(e.target.value)}
                                />
                                <button type="submit" className="auth-submit-btn" style={{ marginTop: 8 }}>
                                    Send Report
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </PublicLayout>
    );
}
